'use client'

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import { FeatureCard } from '@/components/tools/FeatureCard'
import { ToolEntryHero } from '@/components/tools/ToolEntryHero'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'

const SCORE_CATEGORIES = [
  {
    key: 'coreOgTags',
    label: 'Core OG Tags',
    color: '#008dcc',
    pts: '25 pts',
    desc: 'Presence of og:title, og:description, og:image, og:url, og:type, og:site_name. Missing core tags break previews across every share surface.',
  },
  {
    key: 'ogContentQuality',
    label: 'OG Content Quality',
    color: '#FDC304',
    pts: '20 pts',
    desc: 'Title length (30-65 chars), description length (110-200 chars), and og:url vs canonical alignment. Length matters because every platform truncates differently.',
  },
  {
    key: 'twitterCard',
    label: 'Twitter Card',
    color: '#8b5cf6',
    pts: '15 pts',
    desc: 'twitter:card type plus explicit or fallback twitter:title, twitter:description, twitter:image. X falls back to og:* if Twitter-specific tags are missing.',
  },
  {
    key: 'imageReachability',
    label: 'Image Reachability',
    color: '#F90B8A',
    pts: '20 pts',
    desc: 'Live HEAD check on the share image: HTTPS required, must return 200, must be under 5MB. Most platforms refuse anything else and render text-only.',
  },
  {
    key: 'schemaAlignment',
    label: 'Schema Alignment',
    color: '#22c55e',
    pts: '20 pts',
    desc: 'JSON-LD coverage layered on top of OG. AI assistants use Article/Product/Organization/FAQPage for entity disambiguation when OG alone is ambiguous.',
  },
] as const

interface Finding {
  text: string
  status: 'good' | 'warning' | 'bad'
}

interface CategoryResult {
  score: number
  maxScore: number
  color: string
  findings: Finding[]
}

interface OgAuditResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  categories: Record<string, CategoryResult>
  detected: Record<string, string | string[] | boolean | null>
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
}

const STATUS_STYLES: Record<Finding['status'], string> = {
  good: 'text-foreground',
  warning: 'text-foreground/80',
  bad: 'text-foreground',
}

const STATUS_DOT: Record<Finding['status'], string> = {
  good: 'bg-[#22c55e]',
  warning: 'bg-[#FDC304]',
  bad: 'bg-[#F90B8A]',
}

const GRADE_COLOR: Record<OgAuditResult['grade'], string> = {
  A: '#22c55e',
  B: '#008dcc',
  C: '#FDC304',
  D: '#f97316',
  F: '#F90B8A',
}

export default function OpenGraphAuditorPage() {
  const [result, setResult] = useState<OgAuditResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('open-graph-auditor')
  const gate = useToolAccessGate('open-graph-auditor', 'AI Open Graph Auditor')

  // Input validation lives in ToolEntryHero; this receives the trimmed URL.
  async function handleSubmit(trimmed: string) {
    if (!gate.ready) {
      gate.openGate(trimmed)
      return
    }

    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/open-graph-auditor',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(trimmed)
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(data.message || 'Audit failed. Please try again.')
      }

      setResult(data as OgAuditResult)
      gate.markConsumed()

      setTimeout(() => {
        document
          .getElementById('og-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='animate-fade-in'>
      {gate.modal}

      <div className='container mx-auto px-4 py-12'>
        <LenisFadeIn>
          <div className='text-center max-w-3xl mx-auto mb-10'>
            <ToolEntryHero
              title='AI Open Graph Auditor'
              valueProp='Check how your page renders in AI assistants and share previews: Open Graph tags, Twitter Cards, share-image reachability, and JSON-LD coverage.'
              trustLine='Free · one audit per email · instant results'
              inputLabel='Page URL to audit'
              placeholder='example.com or full URL'
              buttonLabel='Audit'
              exampleValue='pixelmojo.io'
              isLoading={isLoading}
              onSubmit={handleSubmit}
            />
            <p className='text-sm text-muted-foreground max-w-2xl mx-auto mt-8'>
              Verify your email, drop in any URL, and get a 100-point breakdown
              with prioritized fixes for the metadata that drives every share
              preview surface.
            </p>
            {error && (
              <p className='text-sm text-[#F90B8A] mt-4 bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {error}
              </p>
            )}
          </div>
        </LenisFadeIn>

        {/* Results */}
        <div id='og-results' className='max-w-4xl mx-auto'>
          {isLoading && (
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8 text-center'>
              <Loader2
                size={32}
                className='animate-spin mx-auto mb-4 text-primary'
              />
              <p className='text-sm text-muted-foreground'>
                Fetching page, parsing meta tags, checking image reachability,
                scanning JSON-LD...
              </p>
            </div>
          )}
          {toolUsage.isGated && !isLoading && (
            <UsageLimitGate
              toolName='AI Open Graph Auditor'
              used={toolUsage.used}
              limit={toolUsage.limit}
            />
          )}
          {result && !isLoading && !toolUsage.isGated && (
            <>
              <div className='bg-muted/50 rounded-2xl border border-line-strong p-8 mb-6 flex flex-col sm:flex-row items-center justify-between gap-6'>
                <div>
                  <p className='text-sm text-muted-foreground leading-none mb-2'>
                    Audited
                  </p>
                  <p className='text-foreground font-mono text-sm break-all'>
                    {result.url}
                  </p>
                </div>
                <div className='flex items-center gap-6'>
                  <div className='text-center'>
                    <p className='text-4xl font-bold text-foreground leading-none'>
                      {result.overallScore}
                      <span className='text-base text-muted-foreground'>
                        /100
                      </span>
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      Overall
                    </p>
                  </div>
                  <div
                    className='h-16 w-16 rounded-2xl flex items-center justify-center text-3xl font-bold text-background'
                    style={{ backgroundColor: GRADE_COLOR[result.grade] }}
                  >
                    {result.grade}
                  </div>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
                {SCORE_CATEGORIES.map(cat => {
                  const cr = result.categories[cat.key]
                  if (!cr) return null
                  const pct = Math.round((cr.score / cr.maxScore) * 100)
                  return (
                    <div
                      key={cat.key}
                      className='bg-background border border-line-strong rounded-2xl p-5'
                    >
                      <div className='flex items-center justify-between mb-3'>
                        <div className='flex items-center gap-2'>
                          <span
                            className='h-2 w-2 rounded-full'
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className='text-sm font-semibold text-foreground leading-none'>
                            {cat.label}
                          </span>
                        </div>
                        <span className='text-sm font-mono text-foreground/80'>
                          {cr.score}/{cr.maxScore}
                        </span>
                      </div>
                      <div className='h-2 w-full rounded-full bg-muted mb-3 overflow-hidden'>
                        <div
                          className='h-full rounded-full'
                          style={{
                            width: `${pct}%`,
                            backgroundColor: cat.color,
                          }}
                        />
                      </div>
                      <ul className='space-y-1.5'>
                        {cr.findings.map((f, i) => (
                          <li
                            key={i}
                            className={`flex items-start gap-2 text-xs leading-relaxed ${STATUS_STYLES[f.status]}`}
                          >
                            <span
                              className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${STATUS_DOT[f.status]}`}
                            />
                            <span>{f.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>

              {result.recommendations.length > 0 && (
                <div className='bg-background border border-line-strong rounded-2xl p-6 mb-6'>
                  <h2 className='text-lg font-semibold text-foreground mb-4'>
                    Recommended fixes
                  </h2>
                  <ol className='space-y-3'>
                    {result.recommendations.map((rec, i) => (
                      <li
                        key={i}
                        className='flex items-start gap-3 text-sm text-foreground leading-relaxed'
                      >
                        <span
                          className='flex-shrink-0 mt-0.5 text-[11px] font-bold rounded-full px-2 py-0.5'
                          style={{
                            backgroundColor:
                              rec.priority === 'high'
                                ? '#F90B8A15'
                                : rec.priority === 'medium'
                                  ? '#FDC30415'
                                  : '#008dcc15',
                            color:
                              rec.priority === 'high'
                                ? '#F90B8A'
                                : rec.priority === 'medium'
                                  ? '#FDC304'
                                  : '#008dcc',
                          }}
                        >
                          {rec.priority.toUpperCase()}
                        </span>
                        <span>
                          <span className='font-semibold'>{rec.category}:</span>{' '}
                          {rec.message}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pre-results info */}
        {!result && !isLoading && (
          <LenisFadeIn>
            <div className='max-w-3xl mx-auto mt-12'>
              <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
                <h2 className='text-lg font-semibold text-foreground mb-4 text-center'>
                  What we analyze
                </h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {SCORE_CATEGORIES.map(item => (
                    <FeatureCard
                      key={item.label}
                      title={item.label}
                      desc={item.desc}
                      color={item.color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </LenisFadeIn>
        )}

        {/* Methodology */}
        <LenisFadeIn>
          <div className='max-w-4xl mx-auto mt-16'>
            <div className='bg-muted/50 rounded-2xl border border-line-strong p-8'>
              <h2 className='text-lg font-semibold text-foreground mb-2 text-center'>
                How We Score (100 Points Total)
              </h2>
              <p className='text-sm text-muted-foreground text-center mb-8 max-w-2xl mx-auto'>
                Your Open Graph score measures whether the metadata that drives
                share previews and AI assistant rendering is present, valid,
                reachable, and consistent with your structured data.
              </p>

              <div className='space-y-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {SCORE_CATEGORIES.map(item => (
                    <div
                      key={item.label}
                      className='bg-background border border-line-strong rounded-xl p-4'
                    >
                      <div className='flex items-center justify-between mb-2'>
                        <div className='flex items-center gap-2'>
                          <span
                            className='h-2 w-2 rounded-full'
                            style={{ backgroundColor: item.color }}
                          />
                          <span className='text-sm font-semibold text-foreground'>
                            {item.label}
                          </span>
                        </div>
                        <span
                          className='text-xs font-bold rounded-full px-2.5 py-0.5'
                          style={{
                            backgroundColor: `${item.color}15`,
                            color: item.color,
                          }}
                        >
                          {item.pts}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className='border-t border-line-strong pt-6'>
                  <h3 className='text-sm font-semibold text-foreground mb-3'>
                    Grade scale
                  </h3>
                  <div className='flex flex-wrap gap-3'>
                    {[
                      { grade: 'A', range: '90-100', color: '#22c55e' },
                      { grade: 'B', range: '75-89', color: '#008dcc' },
                      { grade: 'C', range: '60-74', color: '#FDC304' },
                      { grade: 'D', range: '40-59', color: '#f97316' },
                      { grade: 'F', range: '0-39', color: '#F90B8A' },
                    ].map(g => (
                      <span
                        key={g.grade}
                        className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5'
                        style={{
                          backgroundColor: `${g.color}15`,
                          color: g.color,
                        }}
                      >
                        {g.grade}: {g.range}
                      </span>
                    ))}
                  </div>
                </div>

                <div className='border-t border-line-strong pt-6'>
                  <h3 className='text-sm font-semibold text-foreground mb-3'>
                    Related tools
                  </h3>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    {[
                      {
                        title: 'AEO Page Auditor',
                        href: '/tools/aeo-page-auditor',
                        tag: 'AEO',
                        color: '#7c3aed',
                      },
                      {
                        title: 'AI Crawl Checker',
                        href: '/tools/ai-crawl-checker',
                        tag: '14 Bots',
                        color: '#008dcc',
                      },
                      {
                        title: 'AI Readiness Score',
                        href: '/tools/ai-readiness-score',
                        tag: 'Unified',
                        color: '#8b5cf6',
                      },
                      {
                        title: 'llms.txt Validator',
                        href: '/tools/llms-txt-validator',
                        tag: 'Deep Scan',
                        color: '#FDC304',
                      },
                    ].map(tool => (
                      <a
                        key={tool.href}
                        href={tool.href}
                        className='flex items-start gap-2.5 rounded-lg p-2.5 transition-colors hover:bg-muted'
                      >
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 shrink-0 mt-0.5'
                          style={{
                            backgroundColor: `${tool.color}15`,
                            color: tool.color,
                          }}
                        >
                          {tool.tag}
                        </span>
                        <span className='text-xs text-foreground/80 leading-relaxed'>
                          {tool.title}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LenisFadeIn>
      </div>
    </div>
  )
}
